import { carrierChannel, rqStatus, shipStatus } from './booking';
import { BOOKING_VESSELS } from './fixtures';
import type { BookingRequest } from './types';

// Mint-Booking · màn "2 form" (spOn): form hệ thống (phải) đối chiếu với file hãng / khách đọc được (trái). Dữ liệu dẫn xuất từ một dòng RQ
// theo `grpsOf` / `fileOfRq` của board. Chỉ đọc — chưa ghi DB.

export type FieldState = 'eq' | 'diff' | 'copy' | 'manual' | 'guard' | 'wait';
export interface FormField { g: string; k: string; key: string; val: string; file: string; state: FieldState; ro: boolean }

const needsTemp = (cont: string): boolean => /R[FH]/.test(cont);
const norm = (s: string): string => s.toLowerCase().replace(/[^a-z0-9]/g, '');

type Def = ReadonlyArray<readonly [string, ReadonlyArray<readonly [string, string]>]>;

export function formDefs(r: BookingRequest): Def {
  const ends = r.route.split(' → ');
  const v = BOOKING_VESSELS[r.vessel];
  const vparts = (v?.name ?? '').split(' ');
  const rf = needsTemp(r.cont);
  return [
    ['Booking', [['Pre Booking', r.jobId], ['Booking No', r.bookingNo ?? ''], ['BookingChannel', carrierChannel(r.carrier)], ['SC Number', ''], ['ShipmentStatus', shipStatus(r).k], ['RQStatus', rqStatus(r)]]],
    ['Bên liên quan', [['Khách (ShortName)', r.customer], ['Shipper trên B/L', r.customer.toUpperCase()], ['Customer Ref', ''], ['Sale PIC', r.sale]]],
    ['Hàng', [['TypeShipment', 'FCL'], ['TypeCargo', rf ? 'RF' : 'DC'], ['Nhiệt độ (°C)', rf ? '-18' : ''], ['Thông gió', rf ? 'Closed' : ''], ['Số lượng · TypeCont', r.cont]]],
    ['Vận chuyển', [['POL', ends[0] ?? ''], ['POD', ends[ends.length - 1] ?? ''], ['Carrier', r.carrier], ['Vessel', vparts.slice(0, -1).join(' ')], ['Voyage', vparts.slice(-1)[0] ?? ''], ['CY cut-off', r.cyCutoff ?? ''], ['Doc cut-off', r.siCutoff ?? '']]],
    ['Ghi chú', [['Booking remark', r.remark ?? ''], ['Internal remark', '']]],
  ];
}

/** Giá trị "file đọc được" (mẫu): có số booking → thư hãng đọc đủ; chưa có → thư khách. */
export function fileValues(r: BookingRequest): Record<string, string> {
  const m: Record<string, string> = {};
  for (const [g, fl] of formDefs(r)) {
    for (const [k, v] of fl) {
      if (['ShipmentStatus', 'RQStatus', 'BookingChannel', 'Pre Booking', 'Internal remark', 'Sale PIC'].includes(k)) continue;
      if (!r.bookingNo && ['Booking No', 'Vessel', 'Voyage', 'CY cut-off', 'Doc cut-off', 'Carrier'].includes(k)) continue;
      if (v) m[`${g}|${k}`] = v;
    }
  }
  if (r.cyWarn && r.cyCutoff) m['Vận chuyển|CY cut-off'] = '23/Oct 11:00';
  return m;
}

export function formFields(r: BookingRequest, read: boolean): FormField[] {
  const file = fileValues(r);
  const out: FormField[] = [];
  for (const [g, fl] of formDefs(r)) {
    for (const [k, val] of fl) {
      const key = `${g}|${k}`;
      const f = file[key] ?? '';
      const guard = key === 'Vận chuyển|Carrier';
      const ro = k === 'ShipmentStatus' || k === 'RQStatus';
      let state: FieldState = 'manual';
      if (guard) state = 'guard';
      else if (!read) state = 'wait';
      else if (!f) state = 'manual';
      else if (norm(val) === norm(f)) state = 'eq';
      else state = val.trim() ? 'diff' : 'copy';
      out.push({ g, k, key, val, file: f, state, ro });
    }
  }
  return out;
}

export function fieldCounts(fs: readonly FormField[]): Record<'eq' | 'diff' | 'copy' | 'manual', number> {
  const n = (s: FieldState): number => fs.filter((x) => x.state === s).length;
  return { eq: n('eq'), diff: n('diff'), copy: n('copy'), manual: n('manual') };
}
